"use client";

import { useEffect } from "react";
import { PRIVACY_TRIGGER_ID } from "dds/organisms/footer/footer.constants";

type OneTrustWindow = Window & {
  OneTrust?: { ToggleInfoDisplay?: () => void };
};

/**
 * Opens the OneTrust preference centre from the DDS footer's "Your Privacy Choices" link. Runs
 * ahead of bubble-phase listeners; an earlier capture-phase one that cancels the click still wins.
 * Full rules: `directv-design-system/design-conflicts/footer.mdx`.
 */
export function FooterPrivacyChoices() {
  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }

      const target = event.target as Element | null;
      const trigger = target?.closest?.(`#${PRIVACY_TRIGGER_ID}`);
      if (!trigger) {
        return;
      }

      const { OneTrust } = window as OneTrustWindow;
      if (typeof OneTrust?.ToggleInfoDisplay !== "function") {
        return;
      }

      event.preventDefault();
      try {
        OneTrust.ToggleInfoDisplay();
      } catch {
        const href = trigger.getAttribute("href");
        if (!href) {
          return;
        }
        if (trigger.getAttribute("target") === "_blank") {
          window.open(href, "_blank", "noopener,noreferrer");
        } else {
          window.location.assign(href);
        }
      }
    };

    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, []);

  return null;
}

export default FooterPrivacyChoices;
